import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import {
  View,
  StyleSheet,
  SafeAreaView,
  Dimensions,
  ScrollView,
  Image,
  PanResponder,
  GestureResponderEvent,
  PanResponderGestureState,
} from 'react-native';

// Import assets for web compatibility
import meanderStarsStatic from '../../assets/Meander-stars-wide-new-static.jpg';
const meanderStarsVideo = require('../../assets/meanderstars1440.mp4');
import mountainSplash from '../../assets/mountainSplash.png';
import chargingStation from '../../assets/chargingStation.png';
import {
  getActiveCharges,
  getDriverPlan,
  loginUser,
  oauthLogin,
  registerUser,
  updateUser,
  validateUserToken,
} from '../store/actions/user-actions';
import { notificationService } from '../services/notification-service';
import { isSuccessful } from '../services/api-requests';
import {
  createUserSession,
  UserSessionAttributes,
} from '../models/user-session-model';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import SignInOptions from './SignInOptions';
import { getIpAddressFunc } from '../components/common/utils/ip-address';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { UserInfo } from '../models/user-info';
import { createUser } from '../models/login-register-model';
import { setSession } from '../store/actions/user-session';
import { getAllChargers } from '../store/actions/charging-actions';
import { getUserSession, getUserSessionObj } from '../services/user-session-service';
import { storeUserSession } from '../services/helpers/session-helpers';
import { getUserInfo } from '../services/user-service';
import { AppDispatch } from '../store';
import { RouteNameContext } from '../navigation/RouteNameContext';
import { ChargeAgainContext } from '../navigation/ChargeAgainContext';
import { useLastUsedCharger } from '../hooks/useLastUsedCharger';
import { userActions } from '../store/slices/user-slice';
import RoleService from '../services/role-service';
import { BottomSheetModalRef } from '../components/BottomSheet';
import { useMenu } from '../navigation/MenuContext';
import WelcomeHomeContent from '../components/welcome/WelcomeHomeContent';
import { useWelcomeHeroStyles } from '../components/welcome/useWelcomeHeroStyles';
import HostMobileAppPromptModal from '../components/welcome/HostMobileAppPromptModal';


const { width, height } = Dimensions.get('window');

const WelcomeScreen = ({ navigation, route }: any) => {
  const [ipAddress, setIpAddress] = useState<string | null>('');
  const [userToken, setUserToken] = useState<string>('');
  const [userSession, setUserSession] = useState<
    UserSessionAttributes | undefined
  >(undefined);
  const [isVideoReady, setIsVideoReady] = useState<boolean>(false);
  const [isVideoLoading, setIsVideoLoading] = useState<boolean>(true);
  const [showHostAppPrompt, setShowHostAppPrompt] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Initialize HTML5 video on mount
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const handleLoadedData = () => {
        setIsVideoReady(true);
        setIsVideoLoading(false);
      };
      
      const handleCanPlay = () => {
        video.play().catch((error) => {
          console.log('Video autoplay failed:', error);
        });
      };
      
      video.addEventListener('loadeddata', handleLoadedData);
      video.addEventListener('canplay', handleCanPlay);
      
      // Cleanup event listeners
      return () => {
        video.removeEventListener('loadeddata', handleLoadedData);
        video.removeEventListener('canplay', handleCanPlay);
      };
    }
  }, []);

  //let navDestination = 'MyChargers'
  const session: UserSessionAttributes | undefined = useSelector(
    (state: RootState) => state.user.session
  );
  const isModeChanging = useSelector(
    (state: RootState) => state.user.isModeChanging
  );
  const dispatch: AppDispatch = useDispatch();
  const hasShownChargeAgain = useSelector(
    (state: RootState) => state.user.hasShownChargeAgain
  );
  
  // Menu context for opening side menu (same as native)
  const { openMenu } = useMenu();
  const heroStyles = useWelcomeHeroStyles();

  // Swipe gesture to open menu (swipe right from left edge)
  const panResponder = useMemo(() => PanResponder.create({
    onStartShouldSetPanResponder: (evt: GestureResponderEvent) => {
      // Only respond if touch starts near the left edge (within 40px)
      return evt.nativeEvent.pageX < 40;
    },
    onMoveShouldSetPanResponder: (evt: GestureResponderEvent, gestureState: PanResponderGestureState) => {
      // Respond to horizontal swipes starting from left edge
      return evt.nativeEvent.pageX < 40 && Math.abs(gestureState.dx) > Math.abs(gestureState.dy);
    },
    onPanResponderRelease: (evt: GestureResponderEvent, gestureState: PanResponderGestureState) => {
      // If swiped right more than 50px, open the menu
      if (gestureState.dx > 50) {
        openMenu();
      }
    },
  }), [openMenu]);

  // Navigation context for charge again functionality
  const currentRouteName = useContext(RouteNameContext);
  const chargeAgain = useContext(ChargeAgainContext);
  const showChargeAgain = chargeAgain?.showChargeAgain ?? false;
  const setShowChargeAgain = chargeAgain?.setShowChargeAgain ?? ((_: boolean) => {});
  const { fetchLastUsedCharger, lastUsedCharger, isLoading } =
    useLastUsedCharger() || {};

  const bottomSheetModalRef = useRef<BottomSheetModalRef>(null);
  const OpenBottomSheet = () => {
    if (bottomSheetModalRef && bottomSheetModalRef.current) {
      bottomSheetModalRef.current.present();
    }
  };

  const logout = route?.params?.token;

  // Reset showChargeAgain to false when component mounts
  useEffect(() => {
    if (setShowChargeAgain) {
      setShowChargeAgain(false);
    }
  }, []);

  // Handle charge again visibility based on session changes
  useEffect(() => {
    // console.log('Session changed:', {
    //   userSession: !!userSession,
    //   role: userSession?.role,
    //   hasToken: !!userSession?.access_token,
    // });

    if (
      userSession &&
      !RoleService.isGuest(userSession) &&
      userSession.access_token &&
      userSession.access_token !== ''
    ) {
      // User is authenticated and not guest, fetch last used charger
      console.log('User authenticated, fetching last used charger');
      if (fetchLastUsedCharger) {
        fetchLastUsedCharger();
      }
    } else {
      // User is not authenticated or is guest, hide charge again card
      console.log(
        'User not authenticated or is guest, hiding charge again card'
      );
      if (setShowChargeAgain) {
        setShowChargeAgain(false);
      }
    }
  }, [userSession]);

  // Handle charge again visibility based on lastUsedCharger state
  useEffect(() => {
    if (!isLoading) {
      if (lastUsedCharger && !hasShownChargeAgain) {
        // console.log(
        //   'Last used charger found and card not dismissed, showing charge again card'
        // );
        if (setShowChargeAgain) {
          setShowChargeAgain(true);
        }
      } else {
        // console.log(
        //   'No last used charger or card already dismissed, hiding charge again card'
        // );
        if (setShowChargeAgain) {
          setShowChargeAgain(false);
        }
      }
    }
  }, [lastUsedCharger, isLoading, hasShownChargeAgain]);

  // Reset hasShownChargeAgain when user session changes to a different AUTHENTICATED user
  // Don't reset when logging out (transitioning to undefined/guest)
  useEffect(() => {
    const wasAuthenticatedUser = userSession && !RoleService.isGuest(userSession) && userSession.email;
    const isAuthenticatedUser = session && !RoleService.isGuest(session) && session.email;
    
    // Only reset if BOTH are authenticated users AND they're different users
    if (wasAuthenticatedUser && isAuthenticatedUser && userSession.email !== session.email) {
      console.log(
        'User session changed to different authenticated user, resetting hasShownChargeAgain'
      );
      dispatch(userActions.setHasShownChargeAgain(false));
    }
  }, [userSession?.email, session?.email, dispatch]);

  useEffect(() => {
    if (isModeChanging) {
      console.log(
        'WelcomeScreen: Skipping session update (mode change in progress)'
      );
      return;
    }
    if (!session?.access_token) return;
    const verifySession = async () => {
      const validationResponse = await validateUserToken();
      if (!isSuccessful(validationResponse.status) || !validationResponse.payload) {
        AsyncStorage.clear();
      } else {
        const sessionVerify = validationResponse.payload;
        await AsyncStorage.setItem('token', sessionVerify.access_token);
        await AsyncStorage.setItem('IpAddress', sessionVerify.IpAddress ?? '');
        await AsyncStorage.setItem('email', sessionVerify.email);
        // await AsyncStorage.setItem('role', JSON.stringify(sessionVerify.role));
        await AsyncStorage.setItem('userId', sessionVerify.id);
        await storeUserSession(sessionVerify);
      }
    };
    setUserSession(session);

    if (session) {
      verifySession();
    }
  }, [session, isModeChanging]);

  // OAuth Return Message Listener in a separate useEffect
  useEffect(() => {
    const oauthReturnMessage = async (event: MessageEvent) => {
      //console.log(`processing event.origin: ${event.origin}`);
      // const expectedOrigin = process.env.REACT_NATIVE_API_SERVER;
      // const normalizedOrigin = expectedOrigin?.replace(/:8003$|:8001$/, ''); // Remove default ports
      // const eventOrigin = event.origin.replace(/:8003$|:8001$/, ''); // Normalize event origin
      if (process.env.REACT_NATIVE_ENV === 'dev') {
        if (event.origin !== `https://stage.meanderev.co:8803`) return;
        // } else if(process.env.REACT_NATIVE_ENV === 'stage'){
        //   if (event.origin !== `${process.env.REACT_NATIVE_API_SERVER}`) return;
      } else {
        if (event.origin !== `${process.env.REACT_NATIVE_API_SERVER}:8803`)
          return;
      }

      const profile: UserInfo = event.data as UserInfo;
      const ipAddress = await getIpAddressFunc();
      const { sub, email, firstname, lastname } = profile;
      const authType = event.data.service;

      console.log(`OAuth Debug: ${JSON.stringify(event.data)}`);
      console.log(`OAuth Debug: ${JSON.stringify(event.data.service)}`);

      try {
        // Use the updated loginUser function which handles OAuth routing
        const loginObj = createUserSession({
          email,
          username: email,
          password: sub.toString(), // OAuth sub as password (will be handled securely by loginUser)
          IpAddress: ipAddress || '1.1.1.1',
          logintype: authType,
          service: authType,
          sub: sub.toString(),
        });

        let loginResp = await loginUser(loginObj);

        // If login fails (user doesn't exist), try registration
        if (loginResp === null) {
          const registerObj = createUser({
            email,
            password: '', // OAuth sub as password (will be handled securely by registerUser)
            username: email,
            first_name: firstname,
            last_name: lastname,
            role: ['user'],
            service: authType,
            sub: sub.toString(),
          });

          const registerResp = await registerUser(registerObj);
          
          if (registerResp && Object.keys(registerResp).length > 0) {
            // After registration, try login again
            loginResp = await loginUser(loginObj);
          } else {
            notificationService.sendNotification(
              'error',
              'Registration issue, please try again'
            );
            return;
          }
        }

        if (loginResp) {
          // Validate the token and get proper session data
          const validationResponse = await validateUserToken();
          if (
            isSuccessful(validationResponse.status) &&
            validationResponse.payload?.access_token &&
            validationResponse.payload?.IpAddress
          ) {
            const newSessionData = validationResponse.payload;
            await storeUserSession(newSessionData);
            dispatch(setSession(newSessionData));

            // Fetch active charges after successful login
            dispatch(getActiveCharges());

            // Fetch user info to populate onboarding_tasks/partner_tasks in Redux
            // so the SideMenu can show "Complete Onboarding" immediately after OAuth login
            try {
              const userInfoResp = await getUserInfo();
              if (isSuccessful(userInfoResp.status) && userInfoResp.payload?.user) {
                dispatch(userActions.setUserObj({ userObj: userInfoResp.payload.user }));
              }
            } catch (e) {
              console.error('WelcomeScreen.web OAuth: Error fetching user info:', e);
            }

            const loginObj = createUserSession({
              email,
              username: email,
              IpAddress: ipAddress || '1.1.1.1',
              logintype: authType,
            });

            if (newSessionData.primary_use === 'host') {
              navigation.navigate('MyChargers', {
                userInfo: loginObj,
                auth: authType === 'google' ? 'googleOAuth' : 'appleOAuth',
              });
            } else {
              // On web, open the side menu instead of navigating to MyAccount
              openMenu();
            }

            notificationService.sendNotification(
              'success',
              'Login Successful!'
            );
          } else {
            notificationService.sendNotification(
              'error',
              'Login failed: session data incomplete.'
            );
          }
        }
      } catch (error) {
        console.error('OAuth authentication error:', error);
        
        if (error instanceof Error) {
          if (error.message.includes('different OAuth provider')) {
            notificationService.sendNotification(
              'error',
              'Account exists with different sign-in method. Please use the original sign-in method.'
            );
          } else {
            notificationService.sendNotification(
              'error',
              'Authentication failed. Please try again.'
            );
          }
        } else {
          notificationService.sendNotification(
            'error',
            'Authentication failed. Please try again.'
          );
        }
      }

      dispatch(oauthLogin(profile));

      if (event.data.closeWindow) {
        window.close();
      }
    };

    window.addEventListener('message', oauthReturnMessage);

    return () => {
      window.removeEventListener('message', oauthReturnMessage);
    };
  }, [dispatch, navigation, userSession]);

  // Run these functions only once on component mount
  useEffect(() => {
    const initializeApp = async () => {
      const getUserIp = async () => {
        const ip = await getIpAddressFunc();
        setIpAddress(ip ?? '');
      };

      const isUserSignedIn = async () => {
        const token = await AsyncStorage.getItem('token');
        if (token) {
          setUserToken(token);
          const existingSession = await getUserSession();
          if (existingSession) {
            setUserSession(existingSession);
            dispatch(setSession(existingSession));
          } else {
            const validationResponse = await validateUserToken();

            if (
              validationResponse.status === 401 ||
              !isSuccessful(validationResponse.status) ||
              (validationResponse.payload?.message &&
                (validationResponse.payload.message
                  .toLowerCase()
                  .includes('jwt malformed') ||
                  validationResponse.payload.message
                    .toLowerCase()
                    .includes('invalid token')))
            ) {
              await AsyncStorage.clear();
              dispatch(userActions.clearActiveCharges()); // Clear active charges on invalid session
              if (validationResponse.payload?.message) {
                notificationService.sendNotification(
                  'error',
                  validationResponse.payload.message
                );
              }
            } else if (validationResponse.payload) {
              const validatedSession = validationResponse.payload;
              await AsyncStorage.setItem(
                'userSession',
                JSON.stringify(validatedSession)
              );
              dispatch(setSession(validatedSession));
            }
          }
        } else {
          await AsyncStorage.clear();
          dispatch(userActions.clearActiveCharges()); // Clear active charges when no token
        }
      };

      await getUserIp();
      await isUserSignedIn();
      dispatch(getActiveCharges());

      // Fetch user info to populate onboarding_tasks/partner_tasks in Redux
      // so the SideMenu can show "Complete Onboarding" for returning users
      const currentSession = await getUserSession();
      if (
        currentSession &&
        !RoleService.isGuest(currentSession) &&
        currentSession.access_token &&
        currentSession.access_token !== ''
      ) {
        try {
          const userInfoResp = await getUserInfo();
          if (isSuccessful(userInfoResp.status) && userInfoResp.payload?.user) {
            dispatch(userActions.setUserObj({ userObj: userInfoResp.payload.user }));
          }
        } catch (e) {
          console.error('WelcomeScreen.web: Error fetching user info:', e);
        }
      }
    };

    initializeApp();
  }, [dispatch]);

  // Validate token and fetch chargers when IP address and token are set
  useEffect(() => {
    const validateAndFetchChargers = async () => {
      const validationResp = await validateUserToken();
      if (!isSuccessful(validationResp.status)) {
        await AsyncStorage.clear();
      } else {
        dispatch(getAllChargers());
        dispatch(getDriverPlan());
      }
    };

    if (ipAddress && userToken) {
      validateAndFetchChargers();
    }
  }, [ipAddress, userToken, dispatch]);

  const handleManagePress = async (navDestination: String) => {
    console.log('manage EV pressed');
    //check if user already logged in
    //if user is not logged in, bring to sign in/sign up page
    // if they are logged in, bring to "my chargers" page
    //navDestination = "MyChargers";
    if (userToken && !RoleService.isGuest(userSession)) {
      const validationResponse = await validateUserToken();
      dispatch(getDriverPlan());
      
      //now the session object should be in validationResponse.payload
      if (!isSuccessful(validationResponse.status) || !validationResponse.payload) {
        //notificationService.sendNotification('error', 'You are not logged in!');
        // clear the cache
        const keys = await AsyncStorage.getAllKeys();
        await AsyncStorage.multiRemove(keys);
        OpenBottomSheet();
      } else {
        await storeUserSession(validationResponse.payload);
        // If navDestination is 'MyAccount', open the side menu instead (same as native)
        if (navDestination === 'MyAccount') {
          openMenu();
        } else if (navDestination && navDestination !== '') {
          navigation.navigate(navDestination);
        }
      }
    } else {
      //notificationService.sendNotification('error', 'You are not logged in!');
      OpenBottomSheet();
    }
  };

  const handleHostPress = () => {
    setShowHostAppPrompt(true);
  };

  const handleNavPress = async (tabSelect: string) => {
    const createSession = await getUserSession();
    if (createSession && createSession.access_token !== '') {
      setUserSession(createSession);
      if (RoleService.isHost(createSession)) {
        //change role to user
        dispatch(updateUser(createSession, { primary_use: 'driver' }));
      } else {
        dispatch(setSession(createSession));
      }
      navigation.navigate('ChargerSelection', { tabSelect: tabSelect });
    } else {
      notificationService.sendNotification('error', 'Error Creating Session');
    }
  };

  const { width: windowWidth, height: windowHeight } = Dimensions.get('window');

  // Video sizing - using original settings for proper resolution
  const verticalOffset = -height * 0.25;
  // console.log ("width:", maxVideoWidth);
  // console.log ("height", maxVideoHeight);
  return (
    <SafeAreaView style={styles.container} {...panResponder.panHandlers}>
      <View
        style={[
          styles.containerBgColor,
          { height: height, width: width <= 430 ? width : 430 },
        ]}
      >
        <View style={styles.videoContainer}>
          {/* Show static image while video is loading */}
          {!isVideoReady && (
            <Image
              source={meanderStarsStatic}
              style={[
                styles.video,
                {
                  width: 1440,
                  height: 1440,
                  transform: [
                    { scale: 0.65 },
                    { translateY: verticalOffset },
                  ],
                },
              ]}
            />
          )}
          {/* HTML5 Video for Web */}
          <video
            ref={videoRef}
            src={meanderStarsVideo}
            loop
            muted
            autoPlay
            playsInline
            style={{
              position: 'absolute',
              alignSelf: 'center',
              width: 1440,
              height: 1440,
              transform: `scale(0.65) translateY(${verticalOffset}px)`,
              opacity: isVideoReady ? 1 : 0,
              objectFit: 'contain',
            }}
          />
        </View>
        <View style={styles.overlay}>
          <View style={styles.textBgColor}>
            <View style={heroStyles.blackGapFill} />
            <Image
              source={mountainSplash}
              style={heroStyles.mountainSize}
              resizeMode="contain"
            />
            <Image
              source={chargingStation}
              style={heroStyles.chargingStation}
            />
            <WelcomeHomeContent
              isHost={!!session && RoleService.isHost(session)}
              isAuthenticated={!!session && !RoleService.isGuest(session)}
              onFindCharger={() => handleNavPress('Map')}
              onHostPress={handleHostPress}
              onViewMyChargers={() => handleManagePress('MyChargers')}
              onSignIn={() => handleManagePress('')}
              onOpenMenu={() => handleManagePress('MyAccount')}
            />
          </View>
        </View>
        <SignInOptions
          navigation={navigation}
          bottomSheetModalRef={bottomSheetModalRef}
          destination={
            session?.primary_use === 'host' ? 'MyChargers' : 'MyAccount'
          }
        />
        <HostMobileAppPromptModal
          visible={showHostAppPrompt}
          onClose={() => setShowHostAppPrompt(false)}
        />
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  containerBgColor: {
    backgroundColor: '#1A1D21',
    flex: 1,
  },

  videoContainer: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    overflow: 'hidden',
  },
  video: {
    position: 'absolute',
    alignSelf: 'center',
    //resizeMode: 'contain',
    transform: [
      { scale: 1 },
      { translateY: 0 },
      { translateX: 0 },
      { rotate: '0deg' },
    ],
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'flex-end',
    alignItems: 'stretch',
    overflow: 'visible',
  },
  text: {
    color: '#FFFFFF',
    fontSize: 24,
    fontWeight: 'bold',
    position: 'absolute',
  },
  textBgColor: {
    backgroundColor: '#000000',
    width: '100%',
    alignItems: 'center',
    overflow: 'visible',
  },
});

export default WelcomeScreen;
